import React, { useRef, useEffect } from "react";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import _ from "lodash";

const UsageRight = (props) => {
  const {
    hotelName = "COURTYARD BY MARRIOTT TOKYO GINZA HOTEL",
    propertyCode = "TYOCY",
    photographerName = "Miyuki Kaneko",
    shootDates = "March 28–29, 2024",
    studioPhone = "+81 3 5722 7757",
    email = "kaneko@nacasa.co.jp",
    signatureDate = "May 07, 2024",
    printName = "Miyuki Kaneko",
    title = "Chief Photographer",
    photoshootImages = [],
  } = props;

  const textRef = useRef(null);
  const photosRef = useRef(null);
  const hasRun = useRef(false);

  const capture = async (node) => {
    const images = node.querySelectorAll("img");
    await Promise.all(
      [...images].map(
        (img) =>
          img.complete ||
          new Promise((res) => {
            img.onload = img.onerror = res;
          }),
      ),
    );

    return html2canvas(node, {
      scale: 1.5,
      useCORS: true,
      backgroundColor: "#ffffff",
      letterRendering: true,
    });
  };

  const chunkImages = (arr, size = 16) => {
    const chunks = [];
    for (let i = 0; i < arr.length; i += size) {
      chunks.push(arr.slice(i, i + size));
    }
    return chunks;
  };
  const generatePDF = async () => {
    const pdf = new jsPDF("p", "pt", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    /* ================= PAGE 1 — TEXT ================= */
    const textCanvas = await capture(textRef.current);
    const textImg = textCanvas.toDataURL("image/png");
    const textHeight = (textCanvas.height * pageWidth) / textCanvas.width;

    pdf.addImage(textImg, "PNG", 0, 0, pageWidth, Math.min(textHeight, pageHeight));

    /* ================= IMAGE PAGES ================= */
    //const approvedPhotos = photoshootImages.filter((p) => p.status === "bt-approved");
    const approvedPhotos = _.times(24, () => photoshootImages.filter((p) => p.status === "bt-approved")).flat();
    const imagePages = chunkImages(approvedPhotos, 16);

    for (let i = 0; i < imagePages.length; i++) {
      pdf.addPage();

      // Inject ONLY 12 images into DOM
      photosRef.current.innerHTML = "";
      imagePages[i].forEach((img) => {
        const wrapper = document.createElement("div");
        wrapper.innerHTML = `
        <div style="text-align:center">
          <img src="${process.env.REACT_APP_API_URL}/photoshoot/photo/thumb/${img.fname}"
               style="width:180px;height:180px;object-fit:contain"/>
          <div style="font-size:10px;word-break:break-all">${img.fname}</div>
        </div>
      `;
        photosRef.current.appendChild(wrapper);
      });

      /* ✅ ADD FOOTER FOR EACH IMAGE PAGE */
      const footer = document.createElement("p");
      footer.style.textAlign = "right";
      footer.style.marginTop = "8px";
      footer.style.fontSize = "8px";
      footer.innerHTML = `© ${printName} / Nacása &amp; Partners Inc.`;

      photosRef.current.appendChild(footer);

      const canvas = await capture(photosRef.current);
      const imgData = canvas.toDataURL("image/png");
      const imgHeight = (canvas.height * pageWidth) / canvas.width;

      pdf.addImage(imgData, "PNG", 0, 0, pageWidth, Math.min(imgHeight, pageHeight));
    }

    window.open(pdf.output("bloburl"), "_blank");
  };

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;
    generatePDF();
  }, []);

  return (
    <div style={{ position: "absolute", left: "-9999px", top: "-9999px" }}>
      {/* ================= PAGE 1 : TEXT ================= */}
      <div ref={textRef} className="bg-white text-black p-10" style={{ width: "794px", fontSize: "14px" }}>
        <div className="text-center mb-5" style={{ fontSize: "8px", marginTop: "-20px" }}>
          MARRIOTT CONFIDENTIAL AND PROPRIETARY INFORMATION
        </div>
        <div className="flex flex-col items-center my-2">
          <img src={process.env.PUBLIC_URL + "/images/marriott-logo.png"} alt="Marriott Logo" className="h-12 mb-2" />
        </div>

        <h1 className="text-[18px] text-center mb-5 uppercase font-bold text-red-600">License of Unlimited Usage Rights for Photography</h1>

        <div className="mb-5 grid grid-cols-2 gap-x-1 gap-y-1">
          <div className="col-span-1">Hotel Name: {hotelName}</div>
          <div className="col-span-1">Shoot Date(s): {shootDates}</div>
          <div className="col-span-1">Property / MARSHA Code: {propertyCode}</div>
          <div className="col-span-1">Studio Phone Number: {studioPhone}</div>
          <div className="col-span-1">Studio / Photographer Name: {photographerName}</div>
          <div className="col-span-1">Email Address: {email}</div>
        </div>

        <div className="mb-5">
          <p>
            The owner of the copyright in the photographic image(s) created during the session identified above and identified in the attachment to
            this document (together, the “Works”) represents and warrants that:
          </p>
          <p>(1) The Photographer is the sole creator of the Works;</p>
          <p>(2) The sole author and owner of the copyright in the Works is (check applicable):</p>
          <p className="font-mono">___ the Studio (if the Photographer is an employee of the Studio); or</p>
          <p className="font-mono">___ the Photographer (if the Photographer is not employed by a studio); and</p>
          <p>
            (3) The use of the Works by Marriott International, Inc., its affiliates (including any person or company directly or indirectly
            controlling or controlled by Marriott International, Inc.), the hotel owner and, if the hotel is franchised, the hotel franchisee
            (collectively, "Marriott") and their sublicensees to use the Works will not violate the rights of any third party
          </p>
        </div>

        <div className="mb-5">
          <p>
            In exchange for payment, the sufficiency and receipt of which is acknowledged, the owner of the copyright in the Works grants to Marriott
            a unlimited, perpetual, exclusive, irrevocable worldwide license to distribute, publicly display, alter, edit, retouch and create
            derivative works of the Works in all media now known or hereafter developed and to sublicense the foregoing rights to third parties;
            provided, however, that Marriott may not resell the Works for profit. Neither the Studio nor the Photographer will license (with or
            without compensation) the Works to anyone else or use the works for any purpose other than the Photographer’s personal portfolio.
          </p>
          <p>
            No further permission is required for Marriott and its designees to reproduce or otherwise use the Works for any purpose in any medium,
            without credit or attribution or Copyright Management Information, including without limitation, the following examples:
          </p>
        </div>

        <div className="mb-5">
          <p>
            No further permission is required for Marriott and its designees to reproduce or otherwise use the Works for any purpose in any medium,
            without credit or attribution, including without limitation:
          </p>
          <ul className="list-disc ml-6 mt-2">
            <li>
              for marketing materials, advertising and promotions in all channels and at all levels (including digital and online directories,
              electronic media/brochures, video, print, brochures, publications, newsletters, guides, direct mail, point-of-sale and vehicle
              graphics).
            </li>
            <li>
              for all digital formats, media, and social media sites such as YouTube (including display graphics for television, or other display at
              any location or channel such as hotel properties, trade shows and airport dioramas).
            </li>
            <li>
              for all Internet and Intranet sites, whether owned or controlled by Marriott or by its affiliates and franchisees, or by unrelated third
              parties including without limitation Online Travel Agencies such as expedia.com or hotels.com.
            </li>
            <li>for prints, file replication and framed photographs.</li>
          </ul>
        </div>

        <div className="mb-5">
          <p>
            In the event of a conflict between the terms of this document and the terms of any other document, the terms of this document shall
            prevail. The person signing below represents and warrants that he/she is authorized to execute this letter on behalf of the owner of the
            copyright in the Works (the Photographer or Studio, as indicated above).
          </p>
          <div className="mt-6 grid grid-cols-2 gap-x-6 gap-y-2">
            <div className="col-span-1">Signature: ____________________________</div>
            <div className="col-span-1 text-right">Print Name: {printName}</div>
            <div className="col-span-1">Date: {signatureDate}</div>
            <div className="col-span-1 text-right">Title: {title}</div>
          </div>
        </div>

        <div className="mt-8">
          <p className="mb-2 font-bold text-red-600" style={{ fontSize: "11px" }}>
            <i>
              A copy or list (e.g., contact sheet) of all Work(s) must accompany this letter. This letter must be signed and dated by the owner of the
              copyright in the Works (the Photographer or Studio, as indicated above). 22-August-2022
            </i>
          </p>
          <p className="text-center mb-2" style={{ fontSize: "10px" }}>
            MARRIOT CONFIDENTIAL AND PROPRIETARY INFORMATION. The contents of this material are confidential and proprietary to Marriott
            International, Inc. and may not be reproduced, disclosed, distributed, or used without the express permission of an authorized
            representative of Marriott. Any other use is expressly prohibited.
          </p>

          <div className="my-5"></div>
        </div>

        {/* PHOTOS PAGE */}
        <div
          ref={photosRef}
          style={{
            width: "794px",
            padding: "40px",
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: "16px",
            background: "#fff",
          }}
        ></div>
      </div>
    </div>
  );
};

export default UsageRight;
